import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { PublicUser, AuthTokens } from '@runninglime/shared';
import { apiPost, setTokens, clearTokens, getRefreshToken } from '../lib/api.ts';
import { clearDelegationCache } from '../lib/delegation.ts';

interface AuthContextType {
  user: PublicUser | null;
  loading: boolean;
  isAdmin: boolean;
  isEmployee: boolean;
  hasAdminAccess: boolean;
  mustChangePassword: boolean;
  needsProfile: boolean;
  needsVerification: boolean;
  isRejected: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: { email: string; password: string; companyName: string; contactName: string; inviteToken: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Try to restore session from refresh token on mount
  // Use a ref to prevent StrictMode double-invoke from racing on token rotation
  const restoringRef = React.useRef(false);
  useEffect(() => {
    if (restoringRef.current) return;
    restoringRef.current = true;

    const restore = async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await apiPost<AuthTokens>('/auth/refresh', { refreshToken });
        if (res.success && res.data) {
          setTokens(res.data);
          setUser(res.data.user);
        } else {
          clearTokens();
        }
      } catch {
        clearTokens();
      }
      setLoading(false);
    };

    restore();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiPost<AuthTokens>('/auth/login', { email, password });
    if (res.success && res.data) {
      setTokens(res.data);
      setUser(res.data.user);
      return { success: true };
    }
    return { success: false, error: res.error || 'Login failed' };
  }, []);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    companyName: string;
    contactName: string;
    inviteToken: string;
  }) => {
    const res = await apiPost<AuthTokens>('/auth/register', data);
    if (res.success && res.data) {
      setTokens(res.data);
      setUser(res.data.user);
      return { success: true };
    }
    return { success: false, error: res.error || 'Registration failed' };
  }, []);

  const logout = useCallback(() => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      apiPost('/auth/logout', { refreshToken }).catch(() => {});
    }
    clearTokens();
    clearDelegationCache();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return;
    try {
      const res = await apiPost<AuthTokens>('/auth/refresh', { refreshToken });
      if (res.success && res.data) {
        setTokens(res.data);
        setUser(res.data.user);
      }
    } catch { /* ignore */ }
  }, []);

  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role === 'employee';
  const hasAdminAccess = isAdmin || isEmployee;
  const mustChangePassword = !!user && user.mustChangePassword === true;
  const needsProfile = !!user && user.role === 'customer' && user.verificationStatus === 'pending_profile';
  const needsVerification = !!user && user.role === 'customer' && user.verificationStatus === 'pending_verification';
  const isRejected = !!user && user.role === 'customer' && user.verificationStatus === 'rejected';

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, isEmployee, hasAdminAccess, mustChangePassword, needsProfile, needsVerification, isRejected, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
